import {
    PhArrowLeft,
    PhArrowRight,
    PhArrowUpRight,
    PhBellRinging,
    PhCalendarDots,
    PhCalendarPlus,
    PhCalendarStar,
    PhCaretDown,
    PhCheck,
    PhCheckCircle,
    PhCircleNotch,
    PhClock,
    PhInfo,
    PhEnvelope,
    PhEnvelopeSimple,
    PhFacebookLogo,
    PhIdentificationCard,
    PhKey,
    PhLink,
    PhLock,
    PhMapPin,
    PhNavigationArrow,
    PhSeat,
    PhShareNetwork,
    PhShoppingBag,
    PhTicket,
    PhWarningCircle,
    PhWhatsappLogo,
    PhXLogo,
} from "@phosphor-icons/vue";
import { trackAddToCart, trackBeginCheckout, trackIdle, trackShare, trackViewProduct } from "../Shared/analytics";
import { createApp, currencyFormat } from "../Shared/common";
import { collectionCartPath } from "../Shared/collectionUrls";

import axios from "axios";
import cloneDeep from "lodash.clonedeep";
import startCase from "lodash.startcase";
import { defineAsyncComponent } from "vue";
import { ShareNetwork } from "vue-social-sharing";
import { initCopyShareButtons } from "../Shared/clipboardShare";
import { initCollectionBackNav } from "../Shared/collectionBackNav";
import { chargeCalculationMethod } from "../Shared/Enums/ChargeCalculationMethod";
import { chargeType } from "../Shared/Enums/ChargeType";
import { AxiosRedirectIntercepted } from "../Shared/errors";
import { initImageEnhancements, isLoaded, revealNow } from "../Shared/imageEnhance";
import { Big } from "../Shared/number";
import { CURRENCY_FORMAT_OPTIONS } from "../Shared/VueComponents/numberFormat";
import { orderUrl } from "../Shared/VueComponents/DoorSalesV2/ordersUrl";
import AccessCode from "./Components/access-code.vue";
import ModalRemindMe from "./Components/modal-remind-me.vue";
import RegistrationForm from "./Components/registration-form.vue";
import Throttle from "./Components/throttle.vue";
import Waitlist from "./Components/waitlist.vue";
import IconTile from "../Shared/VueComponents/IconTile.vue";
import SessionPickerLoaderV2 from "./Components/EventV2/SessionPickerLoaderV2.vue";
import SessionSectionV2 from "./Components/EventV2/SessionSectionV2.vue";
import { closedDayNotice, imagesDecoded, initialSessionRows, namedGroupIndex, sameSession, scrollBehavior, selectionFitsRows, selectionWithinRows, sameOriginHref, sessionAddress, sessionDestination, sessionFromResponse, urlForEvent, earliestOnSaleIso, visibleTicketIds, withoutCollection } from "./Components/EventV2/sessionPicker";
import { injectHideyShowyHack } from "./hideyShowyHack";

// IndexPost.Response.StatusType. A rejection is not a reservation failure: it is the cart refusing the
// combination, and it carries no cart id and no redirect.
const ReservationStatus = Object.freeze({
    SucceededExtrasRequired: 0,
    Failed: 1,
    SucceededExtrasDefaulted: 2,
    Rejected: 3,
});

const reservationSucceeded = (status) =>
    status === ReservationStatus.SucceededExtrasRequired || status === ReservationStatus.SucceededExtrasDefaulted;

const SubmissionAction = Object.freeze({
    AddToCart: "AddToCart",
    Checkout: "Checkout",
});

const hasCartExpired = () => {
    var field = "expired";
    var url = window.location.href;
    if (url.indexOf("?" + field + "=") != -1) return true;
    else if (url.indexOf("&" + field + "=") != -1) return true;
    return false;
};

const hasCartSeatFulfillmentFailed = () => {
    var field = "seatFulfillmentFailed";
    var url = window.location.href;
    if (url.indexOf("?" + field + "=") != -1) return true;
    else if (url.indexOf("&" + field + "=") != -1) return true;
    return false;
};

const addCurrentQueryStringToLinks = (element) => {
    const links = element.querySelectorAll("a");
    links.forEach((link) => {
        const originalHref = link.getAttribute("href");

        const isHrefIsAFragment = (originalHref || "").indexOf("#") === 0;
        if (isHrefIsAFragment) return;

        const currentQuerystring = window.location.search.substring(1);
        if (!currentQuerystring || currentQuerystring === "") return;

        const originalHrefHasQuerystring = originalHref.indexOf("?") !== -1;
        const separator = originalHrefHasQuerystring ? "&" : "?";

        const newHref = originalHref + separator + currentQuerystring;
        link.setAttribute("href", newHref);
    });
};

const preLoadeddata = cloneDeep(window.preLoadeddata);
// What the server decided for the page's own night. A session for that night carries these, as one
// for any other night carries what /session decided for it.
const pageNightFlags = {
    hasWaitlist: preLoadeddata.showWaitlist,
    showPreRegistration: preLoadeddata.showPreRegistrationFeature,
    showAccessCodeEntry: preLoadeddata.accessCode?.showEntryBox,
    accessCodeWarning: preLoadeddata.accessCode?.showWarning,
};
function initialSession(ticketGroups) {
    if (!ticketGroups) return null;
    const rows = initialSessionRows(ticketGroups.groups, ticketGroups.startDate, namedGroupIndex(window.location.search));
    return sessionFromResponse(ticketGroups, {groups: rows, ...pageNightFlags});
}

const pageValues = {
    pageInventory: preLoadeddata.inventory,
    pageCharges: preLoadeddata.charges,
    pageDeliveryFeesMessage: preLoadeddata.deliveryFeesMessage ?? "",
    inlineSessions: Boolean(preLoadeddata.ticketGroups),
    pageWaitlistUrl: preLoadeddata.waitlistUrl ?? null,
    pageRegistrationUrl: preLoadeddata.registrationUrl ?? null,
    session: initialSession(preLoadeddata.ticketGroups),
    selectedSessionLabel: "",
    sessionPending: false,
    pickerActive: Boolean(preLoadeddata.hasSessions),
    cartTicketCountByEvent: preLoadeddata.eventId
        ? {[String(preLoadeddata.eventId).toLowerCase()]: preLoadeddata.eventCartTicketCount ?? 0}
        : {},
};
delete preLoadeddata.inventory;
delete preLoadeddata.charges;
delete preLoadeddata.deliveryFeesMessage;
delete preLoadeddata.eventCartTicketCount;

// Resolved UI variant — set on <body data-ui-variant="..."> by _Layout.cshtml.
// The v2 chunks below load only when v2 is the active variant. The few v2 components that
// server markup holds from the first render (icon tiles, swappable sections, the picker's
// loader) are imported with the page, so v1 carries them too.
const isV2 = document.body.dataset.uiVariant === 'v2';

const v2Components = isV2 ? {
    "event-v2-session-section": SessionSectionV2,
    "event-v2-stepper": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/Stepper.vue")),
    "event-v2-access-code": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/AccessCode.vue")),
    "event-v2-remind-me-modal": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/RemindMeModalV2.vue")),
    "event-v2-waitlist": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/WaitlistV2.vue")),
    "event-v2-throttle": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/ThrottleV2.vue")),
    "number-flow-v2": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "../Shared/VueComponents/NumberFlowV2.vue")),
    "add-to-calendar-dialog-v2": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "../Shared/VueComponents/AddToCalendarDialogV2.vue")),
    "dialog-v2": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "../Shared/VueComponents/DialogV2.vue")),
    "icon-tile": IconTile,
    "pre-registration-form-v2": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/PreRegistrationFormV2.vue")),
    "event-v2-session-picker": SessionPickerLoaderV2,
    "event-v2-session-ticket-rows": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/SessionTicketRowsV2.vue")),
} : {};

const vm = createApp({
    data: () =>
        Object.assign(preLoadeddata, {
            currencyFormatOptions: CURRENCY_FORMAT_OPTIONS,
            pageIndex: 1,
            sessionSections: null,
            pageTitle: document.title,
            showModal: false,
            modalTitle: "",
            modalType: "info",
            modalMessage: "",
            busy: false,
            busyAction: null,
            showAddedConfirmation: false,
            canPurchase: true,
            showNoTicketsSelectedAlert: false,
            showMustPurchaseAdditionalTicketsAlert: false,
            showMustPurchaseAdditionalTicketsAlertMessage: "",
            showExpiredAlert: hasCartExpired(),
            showSeatFulfillmentFailedAlert: hasCartSeatFulfillmentFailed(),
            showAddToCalendar: false,
            showRemindMeModal: false,
            autoScanEnabled: preLoadeddata.autoScanPreference,
            chart: null,
            selectedSeats: [],
            itemCartView: [],
            showSeatSelectionCart: false,
            showTicketDialog: false,
            selectedFloor: "",
            holdToken: "",
            bestAvaliableSectionsSelected: {},
            seatsSelectionErrors: [],
            seatSelectionTicketTypeDialogData: {},
            seatSelectionConfirmSelectionCallback: null,
            allSeatIds: {}, // This is currently only being populated when we have a seated table event. This is filled when a section has 'table' in the name
            seatBundles: {}, // This will only be filled with items if a ticketType has the word 'whole' in the name.
            ...pageValues,
            v2CtaMode: "rest",
            // Inline position would beat the max-lg pinning classes.
            v2IsBelowLg: false,
            v2CtaTop: 0,
            v2CtaLeft: 0,
            v2CtaWidth: 0,
            v2CtaHeight: 0,
            v2ShowSelectTicketsButton: false,
            v2CtaAnimated: false,
            v2CtaDigitsAnimated: true,
            descriptionExpanded: {},
            descriptionOverflowing: {},
        }),
    components: {
        Waitlist,
        Throttle,
        AccessCode,
        RegistrationForm,
        ModalRemindMe,
        ShareNetwork,
        ...v2Components,
    },
    mounted() {
        if (this.seatingSettings && this.seatingSettings.seatingChartKey) {
            this.loadSeatsIOScript();
        }
        // One order (== one tab) per cart item; door-sales kiosks allowlist popups, so N open.
        if (this.autoPrintPreference && this.isDoorSales && this.lastCartStatus === 3 && this.lastOrderIds?.length) {
            this.lastOrderIds.forEach(orderId =>
                window.open(orderUrl(this.ordersBaseUrl, orderId, true), "_blank", ""));
        }

        // Simulating an equivalent of document.ready (to be used in bootstrapping google tag manager to subscribe to events etc)
        this.$nextTick(function () {
            const applicationReadyEvent = new CustomEvent("application-ready", {
                detail: {when: Date()},
            });

            if (this.hideyShowyHackEnabled === true) {
                injectHideyShowyHack(this);
            }

            document.body.dispatchEvent(applicationReadyEvent);
            trackViewProduct({id: this.eventId, name: this.eventName});

            if (this.$uiVariant === "v2") {
                this.preselectSoleTicketType();
                this.attachV2InteractionHandlers();
                requestAnimationFrame(() => {
                    this.v2CtaAnimated = true;
                });
            }
        });
        this._idleTimer = setInterval(trackIdle, 60 * 1000);

        // The cart drawer (separate bundle) emits cart:updated when it removes; refetch
        // the cart so both counts stay fresh after an add or delete. The full cart payload
        // carries the per-event breakdown, so one call feeds the per-event notice
        // (eventCartTicketCount) AND the collection-wide CTA gating
        // (collectionCartTicketCount, which only cares whether the cart is empty).
        if (this.collectionId) {
            // cart:updated can fire several times in quick succession (add on this page,
            // remove in the drawer); each handler awaits its own fetch with no ordering
            // guarantee. Tag every fetch and let only the most recent one write, so a slow
            // earlier response can't clobber the counts with stale values.
            this._cartUpdateSeq = 0;
            this._onCartUpdated = async () => {
                const seq = ++this._cartUpdateSeq;
                try {
                    const { data } = await axios.get(`/outlet/api/collection/${this.collectionId}/cart`);
                    if (seq !== this._cartUpdateSeq) return;
                    const events = data?.events ?? [];
                    const sumTickets = (event) =>
                        (event?.tickets ?? []).reduce((total, ticket) => total + ticket.quantity, 0);
                    this.collectionCartTicketCount = events.reduce(
                        (total, event) => total + sumTickets(event), 0);
                    this.cartTicketCountByEvent = Object.fromEntries(
                        events.map((event) => [String(event.eventId ?? "").toLowerCase(), sumTickets(event)]));
                    this.cancelCartUrl = data?.cartId ? `/outlet/carts/${data.cartId}/cancel-cart` : "";
                } catch (err) {
                    if (err?.response?.status === 404 && seq === this._cartUpdateSeq) {
                        this.collectionCartTicketCount = 0;
                        this.cartTicketCountByEvent = {};
                        this.cancelCartUrl = "";
                    }
                    // leave the counts as-is on a transient fetch failure
                }
            };
            window.EventBus?.$on("cart:updated", this._onCartUpdated);
        }

        // Answer the drawer's resync query and emit once now so neither bundle
        // misses the suppression state regardless of mount order.
        this._onSuppressQuery = () => this.emitSuppressState();
        window.EventBus?.$on("cart:suppress-query", this._onSuppressQuery);
        this.emitSuppressState();
    },
    beforeUnmount() {
        if (this._v2OnScroll) {
            window.removeEventListener("scroll", this._v2OnScroll);
            window.removeEventListener("resize", this._v2OnScroll);
            this._v2OnScroll = null;
        }
        if (this._v2OnDrawerHeight) {
            window.EventBus?.$off("cart:drawer-height", this._v2OnDrawerHeight);
            this._v2OnDrawerHeight = null;
        }
        this._v2ScheduleCtaUpdate = null;
        if (this._v2CtaRafId != null) {
            window.cancelAnimationFrame(this._v2CtaRafId);
            this._v2CtaRafId = null;
        }
        if (this._v2ResizeObserver) {
            this._v2ResizeObserver.disconnect();
            this._v2ResizeObserver = null;
        }
        if (this._seatPickerResizeObserver) {
            this._seatPickerResizeObserver.disconnect();
            this._seatPickerResizeObserver = null;
        }
        if (this._seatPickerResizeTimeout) {
            clearTimeout(this._seatPickerResizeTimeout);
            this._seatPickerResizeTimeout = null;
        }
        if (this._v2OnDescriptionResize) {
            window.removeEventListener("resize", this._v2OnDescriptionResize);
            this._v2OnDescriptionResize = null;
        }
        if (this._v2DescriptionRaf) {
            cancelAnimationFrame(this._v2DescriptionRaf);
            this._v2DescriptionRaf = null;
        }
        if (this._v2OnAnchorClick) {
            const root = this._v2AnchorRoot || document;
            root.removeEventListener("click", this._v2OnAnchorClick);
            this._v2OnAnchorClick = null;
            this._v2AnchorRoot = null;
        }
        if (this._onCartUpdated) {
            window.EventBus?.$off("cart:updated", this._onCartUpdated);
            this._onCartUpdated = null;
        }
        if (this._onSuppressQuery) {
            window.EventBus?.$off("cart:suppress-query", this._onSuppressQuery);
            this._onSuppressQuery = null;
        }
        if (this._addedConfirmationTimer) {
            clearTimeout(this._addedConfirmationTimer);
            this._addedConfirmationTimer = null;
        }
        if (this._v2DigitsTimer) {
            clearTimeout(this._v2DigitsTimer);
            this._v2DigitsTimer = null;
        }
        if (this._idleTimer) {
            clearInterval(this._idleTimer);
            this._idleTimer = null;
        }
        if (this._v2ScrollSettleTimer) {
            clearTimeout(this._v2ScrollSettleTimer);
            this._v2ScrollSettleTimer = null;
        }
        if (this._v2OnScrollEnd) {
            window.removeEventListener("scrollend", this._v2OnScrollEnd);
            this._v2OnScrollEnd = null;
        }
        // On :root, so they outlive this app unless cleared.
        ["--v2-cta-bottom", "--v2-header-bottom", "--v2-cta-dock-height"].forEach((name) =>
            document.documentElement.style.removeProperty(name));
    },
    watch: {
        anyTicketsSelected(count, previous) {
            if (this.$uiVariant !== "v2" || (count > 0 && previous > 0)) return;
            // The bar toggles on transform and opacity, so its ResizeObserver never fires.
            this._v2ScheduleCtaUpdate?.();
            this.v2CtaDigitsAnimated = false;
            clearTimeout(this._v2DigitsTimer);
            this._v2DigitsTimer = setTimeout(() => {
                this.v2CtaDigitsAnimated = true;
            }, 320);
        },

        // clear an alerts whenever there are changes
        // to requested inventory
        requestedInventory: {
            handler() {
                this.showNoTicketsSelectedAlert = false;
                this.showMustPurchaseAdditionalTicketsAlert = false;
                (this.showMustPurchaseAdditionalTicketsAlertMessage = ""), (this.showExpiredAlert = false);
                this.accessCode.showWarning = false;

                // v2 dependent-ticket reactive zeroing: when a prerequisite ticket is
                // decremented back to zero, any dependent ticket counts get cleared so
                // the user never sees an orphan that would fail submit-time validation.
                // Submit-time validation in ticketsRequiringOtherInventoriesToBeSelected()
                // still acts as a backstop for any code path that bypasses this watcher.
                if (this.$uiVariant === "v2") {
                    Object.keys(this.inventory).forEach((id) => {
                        if (this.requestedInventory[id] > 0 && this.isLocked(id)) {
                            this.requestedInventory[id] = 0;
                        }
                    });
                }

                const requestedInventoryChangedEvent = new CustomEvent("requested-inventory-changed", {detail: {when: Date(), vm: this}});
                document.body.dispatchEvent(requestedInventoryChangedEvent);
            },
            deep: true,
        },
        autoScanPreference: {
            handler() {},
            deep: true,
        },
        sessionSections() {
            this.$nextTick(() => {
                const page = document.getElementById("events-page");
                initImageEnhancements(page);
                initCopyShareButtons(page);
            });
        },
        // Paging swaps the ticket cards, so mount-time measurements are stale.
        pageIndex() {
            if (this.$uiVariant !== "v2") return;
            this.$nextTick(() => this.measureTicketDescriptions());
        },
        // Hide the shared cart drawer (separate bundle) while adding to an existing
        // cart so only the sticky footer shows; restore it once the selection clears.
        shouldSuppressCartDrawer() {
            this.emitSuppressState();
        },
    },
    computed: {
        inventory() {
            return this.session ? this.session.inventory : this.pageInventory;
        },
        charges() {
            return this.session ? this.session.charges : this.pageCharges;
        },
        deliveryFeesMessage() {
            return this.session ? this.session.deliveryFeesMessage : this.pageDeliveryFeesMessage;
        },
        sessionEventId() {
            return this.session?.eventId ?? this.eventId;
        },
        sessionEventName() {
            return this.session?.name || this.eventName;
        },
        reserveUrl() {
            const url = this.session?.url;
            if (!url) return null;
            return this.sessionInCollection ? url : withoutCollection(url);
        },
        preselectedTicketIds() {
            return Object.keys(this.requestedInventory).filter((id) => this.requestedInventory[id] > 0);
        },

        sessionInCollection() {
            return Boolean(this.collectionId) && (this.session?.url == null || this.session.inCollection === true);
        },
        checkoutAvailable() {
            return !this.inlineSessions || Boolean(this.session?.anyAvailableTicketsOnSale);
        },
        sessionIsPageNight() {
            if (!this.session) return true;
            return !this.session.placeholder && String(this.session.eventId ?? "").toLowerCase() === String(this.eventId ?? "").toLowerCase();
        },
        // The session showing is waitlisted: its waitlist card takes the ticket rows' place, and
        // switching to or from it is an ordinary in-place swap.
        sessionWaitlisted() {
            return this.inlineSessions && Boolean(this.session?.hasWaitlist) && !this.session.placeholder;
        },
        sessionWaitlistUrl() {
            return urlForEvent(this.pageWaitlistUrl, this.sessionEventId);
        },
        // With sessions, the reminder and the access code box follow the session showing, as they
        // would had its event been loaded; without, they stay the page's, as they always have.
        preRegistrationOpen() {
            if (this.inlineSessions) return Boolean(this.session?.showPreRegistration) && !this.session.placeholder;
            return Boolean(this.showPreRegistrationFeature) && this.sessionIsPageNight;
        },
        sessionRegistrationUrl() {
            return urlForEvent(this.pageRegistrationUrl, this.sessionEventId);
        },
        sessionOnSaleIso() {
            return earliestOnSaleIso(this.session);
        },
        sessionAccessCodeEntry() {
            return this.inlineSessions && Boolean(this.session?.showAccessCodeEntry) && !this.session.placeholder;
        },
        sessionUnavailableNotice() {
            if (!this.session || this.session.anyAvailableTicketsOnSale) return null;
            return this.session.ticketsUnavailableNotice;
        },
        eventCartTicketCount() {
            return this.cartTicketCountByEvent[String(this.sessionEventId ?? "").toLowerCase()] ?? 0;
        },
        numberOfTicketsSelected() {
            const itemsNotBundled = this.selectedSeats.filter((x) => !x.isBundle);
            const unBundleItemCount = Object.values(itemsNotBundled).reduce((total, item) => {
                return total + item.count;
            }, 0);

            return unBundleItemCount + Object.keys(this.seatBundles).length;
        },
        showTotal() {
            return this.anyTicketsSelected;
        },
        reserveSeatsSelected() {
            const totalValues = this.selectedSeats;
            const itemsNotBundled = totalValues.filter((x) => !x.isBundle);
            return [...itemsNotBundled, ...Object.values(this.seatBundles)];
        },
        selectedTickets() {
            return Object.keys(this.requestedInventory)
                .filter((inventoryId) => this.requestedInventory[inventoryId] > 0)
                .map((inventoryId) => {
                    const quantity = this.requestedInventory[inventoryId];
                    const inventoryItem = this.inventory[inventoryId];
                    const priceBase = Big(inventoryItem.price).minus(inventoryItem.bookingFee);

                    return {
                        inventoryId,
                        quantity,
                        name: inventoryItem.name,
                        price: inventoryItem.price,
                        total: inventoryItem.price * quantity,
                        bookingFee: inventoryItem.bookingFee,
                        totalBookingFee: inventoryItem.bookingFee * quantity,
                        requiresAnyOfTheseInventoriesToBeSelected: inventoryItem.requiresAnyOfTheseInventoriesToBeSelected,
                        priceBase: priceBase.toNumber(),
                    };
                });
        },

        requestedInventoryToPost() {
            return this.selectedTickets.reduce((obj, ticket) => {
                obj[ticket.inventoryId] = ticket.quantity;
                return obj;
            }, {});
        },

        anyTicketsSelected() {
            return this.selectedTickets.length;
        },

        shouldSuppressCartDrawer() {
            return Boolean(this.collectionId) && this.collectionCartTicketCount > 0 && this.anyTicketsSelected > 0;
        },

        ctaFooterClass() {
            // Pinned by max-lg classes below lg; a position here would fight them.
            if (this.v2IsBelowLg) return "emphasis-floating";
            if (this.v2CtaMode === "bottom") return "sticky rounded-2xl emphasis-floating";
            if (this.v2CtaMode === "top") return "fixed rounded-2xl emphasis-floating";
            return "rounded-b-2xl bg-raised";
        },

        ctaFooterStyle() {
            if (this.v2IsBelowLg || this.v2CtaMode !== "top") return {};
            return this.dockedTopStyle;
        },

        dockedTopStyle() {
            // bottom:auto cancels the class-level `bottom` so a fixed top + bottom
            // can't stretch the element to fill the viewport.
            if (this.v2IsBelowLg) return {};
            return { top: `${this.v2CtaTop}px`, left: `${this.v2CtaLeft}px`, width: `${this.v2CtaWidth}px`, bottom: "auto" };
        },

        total() {
            return this.selectedTickets.map((ticket) => ticket.total).reduce((total, current) => total + current, 0);
        },

        totalBookingFees() {
            return this.selectedTickets
                .map((ticket) => ticket.totalBookingFee)
                .reduce((totalBookingFee, current) => totalBookingFee + current, 0);
        },

        showNextButton() {
            return !this.inlineSessions && this.pageIndex < this.totalNumberOfPages;
        },

        showPreviousButton() {
            return !this.inlineSessions && this.totalNumberOfPages > 1 && this.showGetTicketsButton;
        },

        showGetTicketsButton() {
            return !this.showNextButton;
        },

        // Back and Continue ride in the bar, so a paginated event must keep it.
        ctaHasNothingToShow() {
            return !this.anyTicketsSelected && this.pageIndex === 1 && !this.showNextButton;
        },

        showTransactionFeeMessage() {
            return this.charges && this.charges.length > 0;
        },

        totalTransactionFeeAmount() {
            if (!this.charges || this.charges.length === 0) return 0;

            return this.charges.reduce((totalFee, charge, index) => {
                const baseAmount =
                    charge.calculationMethod === chargeCalculationMethod.highestTicketPrice ? this.getHighestTicketPriceBase() : this.total;

                const baseAmountBig = Big(baseAmount);
                const applicableBracket = charge.chargeBrackets
                    ?.sort((a, b) => b.minimumAmount - a.minimumAmount)
                    ?.find((bracket) => baseAmountBig.gte(bracket.minimumAmount));

                if (!applicableBracket) {
                    return totalFee;
                }

                const chargeAmount =
                    charge.chargeType === chargeType.percentage
                        ? baseAmount <= 0
                            ? 0
                            : Big(applicableBracket.amount).div(100).times(baseAmount).toNumber()
                        : applicableBracket.amount;

                return totalFee + chargeAmount;
            }, 0);
        },
        showSeatSelection() {
            return this.showSeatSelectionCart;
        },
        showPicker() {
            return this.showTicketDialog;
        },
        everythingIsFree() {
            return Object.values(this.inventory)
                .filter((item) => item.isOnSale && item.isAvailable)
                .every((item) => Number(item.price) === 0);
        },

        totalTransactionFeeMessage() {
            if (this.showTransactionFeeMessage) {
                if (this.anyTicketsSelected) {
                    const feeAmount = this.totalTransactionFeeAmount;
                    if (feeAmount > 0) {
                        return `a transaction fee of $${feeAmount.toFixed(2)}`;
                    }
                } else if (this.charges && this.charges.length > 0) {
                    const lowestPossibleAmount = this.getLowestPossibleTransactionFee();
                    if (lowestPossibleAmount > 0) {
                        return `a transaction fee from $${lowestPossibleAmount.toFixed(2)}`;
                    }
                }
            }
            return "";
        },
        disableUIInteraction() {
            if (this.seatingSettings != null) {
                return this.busy || !this.canPurchase;
            }

            return this.busy || this.sessionPending;
        },

        isAddToCartBusy() {
            return this.busyAction === SubmissionAction.AddToCart;
        },

        isCheckoutBusy() {
            return this.busyAction === SubmissionAction.Checkout;
        },

        isCollectionMode() {
            return this.multiEventPurchasingEnabled && this.collectionId && !this.isSeatedEvent;
        },

        cartPageUrl() {
            return this.collectionId ? collectionCartPath(this.collectionId) : null;
        },
    },
    methods: {
        // v2 page interactions: sticky-CTA gating + floating shadow when the ticket
        // card extends past the viewport bottom, and smooth scroll for in-page
        // anchor links (e.g. the "Event details ↗" link in the left card).
        attachV2InteractionHandlers() {
            // Copy-link buttons in the v2 share rows.
            initCopyShareButtons(document.getElementById("events-page"));
            initCollectionBackNav(document.getElementById("events-page"));

            const ticketCard = this.$refs.ticketForm ? this.$refs.ticketForm.parentElement : null;
            this._v2TicketCard = ticketCard;
            if (ticketCard) {
                const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
                const belowLg = window.matchMedia("(width < 64rem)");
                // A custom property on :root invalidates style document-wide, and this
                // runs per scroll frame, so only write when the value moved.
                const publishedVars = {};
                const publish = (name, value) => {
                    if (publishedVars[name] === value) return;
                    publishedVars[name] = value;
                    document.documentElement.style.setProperty(name, value);
                };
                const firstTicketBottom = () => {
                    for (const row of ticketCard.querySelectorAll('[data-testid="ticket-block"]')) {
                        if (row.offsetParent !== null) return row.getBoundingClientRect().bottom;
                    }
                    return -Infinity;
                };

                const update = () => {
                    const rect = ticketCard.getBoundingClientRect();
                    const vh = window.innerHeight;
                    const gap = remPx * 0.75;

                    // Not --cart-drawer-height: it outlives the drawer it describes.
                    const dockHeight =
                        parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--v2-drawer-height")) || 0;
                    const visibleBottom = vh - (dockHeight ? dockHeight + gap : 0);
                    const ctaBottom = dockHeight ? dockHeight + gap : gap;
                    publish("--v2-cta-bottom", `${ctaBottom}px`);

                    // The sticky bar (header's parent) also wraps the preview-mode
                    // banner, so measure it — not just the header — to dock below both.
                    const headerEl = document.querySelector(".event-page-v2-header");
                    const headerBar = headerEl ? headerEl.parentElement : null;
                    const headerBottom = headerBar ? headerBar.getBoundingClientRect().bottom : 0;
                    // Lets the sticky tabs clear the bar, whose height varies.
                    publish("--v2-header-bottom", `${headerBottom}px`);
                    const footer = this.$refs.ctaFooter;
                    if (footer) this.v2CtaHeight = footer.offsetHeight;

                    // Below lg it covers the page end, so the site footer pads against it.
                    this.v2IsBelowLg = belowLg.matches;
                    publish(
                        "--v2-cta-dock-height",
                        this.v2IsBelowLg && this.anyTicketsSelected ? `${this.v2CtaHeight}px` : "0px");

                    this.v2CtaTop = headerBottom + gap;
                    this.v2CtaLeft = rect.left;
                    this.v2CtaWidth = rect.width;

                    // Footer docks at the bottom while the card extends below the fold,
                    // then pins below the header once it would scroll out of view, so the
                    // CTA stays reachable past a long description.
                    if (this.anyTicketsSelected) {
                        this.v2ShowSelectTicketsButton = false;
                        // Dead-band on each exit: docking shrinks the card, which would
                        // otherwise bounce rect.bottom back across the threshold and flap.
                        const band = remPx;
                        const topEdge = headerBottom + this.v2CtaHeight + gap;
                        if (this.v2CtaMode === "bottom") {
                            if (rect.bottom <= visibleBottom - band)
                                this.v2CtaMode = rect.bottom < topEdge ? "top" : "rest";
                        } else if (this.v2CtaMode === "top") {
                            if (rect.bottom >= topEdge + band)
                                this.v2CtaMode = rect.bottom > visibleBottom ? "bottom" : "rest";
                        } else {
                            if (rect.bottom > visibleBottom) this.v2CtaMode = "bottom";
                            else if (rect.bottom < topEdge) this.v2CtaMode = "top";
                        }

                        // Sticky and static both keep flow space, so this can't flap.
                        const dockedTop = vh - ctaBottom - this.v2CtaHeight;
                        if (this.v2CtaMode === "bottom" && dockedTop < firstTicketBottom()) {
                            this.v2CtaMode = "rest";
                        }
                    } else {
                        this.v2CtaMode = "rest";
                        this.v2ShowSelectTicketsButton = rect.bottom < headerBottom + gap;
                    }
                };
                // One update() per frame: it forces layout three times, and this also
                // breaks the ResizeObserver re-entry that throws "loop completed with
                // undelivered notifications".
                this._v2CtaRafId = null;
                const scheduleUpdate = () => {
                    if (this._v2CtaRafId != null) return;
                    this._v2CtaRafId = window.requestAnimationFrame(() => {
                        this._v2CtaRafId = null;
                        update();
                    });
                };

                window.addEventListener("scroll", scheduleUpdate, {passive: true});
                window.addEventListener("resize", scheduleUpdate);
                update();
                this._v2OnScroll = scheduleUpdate;
                this._v2OnDrawerHeight = scheduleUpdate;
                this._v2ScheduleCtaUpdate = scheduleUpdate;
                window.EventBus?.$on("cart:drawer-height", this._v2OnDrawerHeight);

                // Stepper changes grow/shrink the card without a scroll or resize
                // event, so observe its size directly.
                if (typeof ResizeObserver !== "undefined") {
                    this._v2ResizeObserver = new ResizeObserver(scheduleUpdate);
                    this._v2ResizeObserver.observe(ticketCard);
                    // The footer resizes without a scroll or card resize, so observe it
                    // too to keep v2CtaHeight fresh.
                    const footerEl = this.$refs.ctaFooter;
                    if (footerEl) this._v2ResizeObserver.observe(footerEl);
                }
            }

            // Scoped to the v2 page root so smooth-scroll only intercepts hash
            // anchors inside the event page, not unrelated anchors elsewhere in
            // the document (e.g. modals, share widgets).
            const anchorRoot = document.getElementById("events-page");
            if (anchorRoot) {
                const onAnchorClick = (event) => {
                    const link = event.target.closest && event.target.closest('a[href^="#"]');
                    if (!link) return;
                    const hash = link.getAttribute("href");
                    if (!hash || hash === "#") return;
                    const target = document.querySelector(hash);
                    if (!target) return;
                    event.preventDefault();

                    this.scrollBelowStickyBar(target);
                };
                anchorRoot.addEventListener("click", onAnchorClick);
                this._v2OnAnchorClick = onAnchorClick;
                this._v2AnchorRoot = anchorRoot;
            }

            this.$nextTick(() => this.measureTicketDescriptions());
            const onDescriptionResize = () => {
                if (this._v2DescriptionRaf) cancelAnimationFrame(this._v2DescriptionRaf);
                this._v2DescriptionRaf = requestAnimationFrame(() => this.measureTicketDescriptions());
            };
            window.addEventListener("resize", onDescriptionResize, {passive: true});
            this._v2OnDescriptionResize = onDescriptionResize;
        },

        // Offset the scroll past the sticky bar and whatever CTA docks below
        // it (pinned footer when a selection exists, else the recall button).
        scrollBelowStickyBar(target) {
            if (!target) return;
            const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
            const bar = document.querySelector(".event-page-v2-header");
            const barBottom = bar && bar.parentElement ? bar.parentElement.getBoundingClientRect().bottom : 0;
            const ctaHeight = this.anyTicketsSelected ? (this.v2CtaHeight || 3 * remPx) : 2.75 * remPx;
            const offset = barBottom + ctaHeight + remPx * 1.5;
            const top = window.scrollY + target.getBoundingClientRect().top - offset;
            window.scrollTo({top: Math.max(0, top), behavior: scrollBehavior()});
        },

        onSessionPending({label}) {
            this.sessionPending = true;
            this.selectedSessionLabel = label;
        },

        onSessionClosed({kind, eventId, label, url}) {
            this.nextSessionTurn();
            this.sessionPending = false;
            this.selectedSessionLabel = label;
            const notice = closedDayNotice(kind, label);
            this.session = {
                ...sessionFromResponse(null),
                eventId,
                placeholder: true,
                ticketsUnavailableNotice: {...notice, actionUrl: url || null},
            };
            this.requestedInventory = {};
        },

        onSessionSelected({url, isCurrent, inCollection, initial, automatic, label, groupIndex, pageToShowOn, groups, response, sections, title, hasSeatMap, requiresNonce, hasWaitlist, showPreRegistration, showAccessCodeEntry, accessCodeWarning}) {
            if (initial && !isCurrent) return;
            this.sessionPending = false;
            this.selectedSessionLabel = label;

            const destination = sessionDestination({url, isCurrent, inline: this.inlineSessions, hasSeatMap, requiresNonce, hasWaitlist, inCollection});
            if (destination) {
                const href = sameOriginHref(destination, window.location.origin);
                if (href) window.location.href = href;
                return;
            }

            if (!this.inlineSessions) {
                if (initial || automatic) return;
                if (pageToShowOn != null && pageToShowOn !== this.pageIndex) this.pageIndex = pageToShowOn;
                this.$nextTick(() => {
                    const el = groupIndex == null ? document.getElementById("select-tickets") : document.getElementById(`ticket-group-${groupIndex}`);
                    if (groupIndex != null && el) this.revealTicketGroup(el);
                    this.scrollBelowStickyBar(el);
                });
                return;
            }

            // The page's own night was decided by the server when the page loaded; any other night by
            // /session when it was picked. Either way the session carries what the event page would show.
            const flags = isCurrent ? pageNightFlags : {hasWaitlist, showPreRegistration, showAccessCodeEntry, accessCodeWarning};
            let next = sessionFromResponse(response, {url: isCurrent ? null : url, groups, inCollection, ...flags});
            const turn = this.nextSessionTurn();
            if (sameSession(next, this.session)) return;

            const sameEvent = !this.session?.placeholder && next.eventId === this.session?.eventId;
            const keepQuantities = isCurrent && initial;
            if (keepQuantities && !selectionFitsRows(this.requestedInventory, next.groups)) {
                next = sessionFromResponse(response, {url: null, inCollection, ...flags});
            }
            const apply = () => {
                if (turn !== this._sessionTurn) return;
                this.session = next;
                if (!keepQuantities) {
                    this.requestedInventory = sameEvent
                        ? selectionWithinRows(this.requestedInventory, next.groups, Object.keys(next.inventory))
                        : this.emptySelection(next.inventory);
                }
                this.sessionSections = isCurrent ? null : sections;
                document.title = isCurrent || !title ? this.pageTitle : title;
                if (!initial && !sameEvent) trackViewProduct({id: next.eventId, name: next.name});
                if (!initial) window.history.replaceState(window.history.state, "", sessionAddress(url, window.location.href, groupIndex));
            };
            if (initial || sameEvent) apply();
            else this.swapEventInView(apply);
        },

        nextSessionTurn() {
            this._sessionTurn = (this._sessionTurn ?? 0) + 1;
            return this._sessionTurn;
        },

        swapEventInView(apply) {
            const swap = async () => {
                apply();
                await this.$nextTick();
                const images = [...document.querySelectorAll("img[data-session-image]")];
                images.forEach((image) => (image.loading = "eager"));
                await imagesDecoded(images);
                images.filter(isLoaded).forEach(revealNow);
            };
            if (document.startViewTransition) document.startViewTransition(swap);
            else swap();
        },

        onPickerUnavailable() {
            this.nextSessionTurn();
            this.pickerActive = false;
            this.sessionPending = false;
            this.sessionSections = null;
            document.title = this.pageTitle;
            if (!this.ticketGroups) return;
            const whole = sessionFromResponse(this.ticketGroups, pageNightFlags);
            if (sameSession(whole, this.session)) return;
            this.session = whole;
            const sole = this._solePreselectId;
            if (sole && Object.entries(this.requestedInventory).every(([id, quantity]) => id === sole || !(quantity > 0))) {
                this.requestedInventory[sole] = 0;
                this._solePreselectId = null;
                this.preselectSoleTicketType();
            }
        },

        emptySelection(inventory) {
            return Object.fromEntries(Object.keys(inventory).map((id) => [id, 0]));
        },

        loadSeatsIOScript() {
            const script = document.createElement("script");
            script.src = "https://cdn-oc.seatsio.net/chart.js";
            script.async = true;

            script.addEventListener("load", () => {
                this.loadSeatPicker();
            });

            document.body.appendChild(script);
            window.addEventListener(
                "message",
                (object) => {
                    if (object.origin != "https://cdn-oc.seatsio.net") return;
                    try {
                        var data = JSON.parse(object.data);
                        if (data.messageType == "updateSeats") {
                            if (data.label.toLowerCase().includes("table")) {
                                if (!this.allSeatIds[data.parent]) {
                                    this.allSeatIds[data.parent] = [data.label];
                                } else {
                                    this.allSeatIds[data.parent].push(data.label);
                                }
                            }
                        }
                    } catch (e) {
                        return false;
                    }
                },
                false
            );
        },
        showHideSeatSelect() {
            this.showSeatSelectionCart = !this.showSeatSelectionCart;
        },
        getValidators() {
            var validators = [];

            if (this.seatingSettings.enforceConsecutiveSeats) {
                validators.push({
                    type: "consecutiveSeats",
                });
            }

            if (this.seatingSettings.enforceNoOrphanedSeats) {
                validators.push({
                    type: "noOrphanSeats",
                });
            }

            return validators;
        },
        loadSeatPicker() {
            if (!document.getElementById("seatsPicker")) {
                return;
            }
            const tickets = this.seatingSettings.seatedCategories.map((x) => {
                return {
                    category: x.ticketPoolId,
                    ticketTypes: x.ticketTypes,
                };
            });

            const ticketTypes = tickets.map((x) => {
                return x.ticketTypes;
            });

            const ticketTypesFlattened = ticketTypes.concat.apply([], ticketTypes);

            const maximumSelectedItems = ticketTypesFlattened.map((ticket) => {
                return {
                    ticketType: ticket.ticketType,
                    quantity: this.inventory[ticket.ticketType].maximumQuantity,
                };
            });

            this.chart = new window.seatsio.SeatingChart({
                session: "start",
                divId: "seatsPicker",
                workspaceKey: this.seatingSettings.workspaceKey,
                event: this.eventId,
                pricing: tickets,
                priceFormatter: function (price) {
                    return "$" + price;
                },
                maxSelectedObjects: maximumSelectedItems,
                onObjectSelected: this.reserveSeat,
                onObjectDeselected: this.unReserveSeat,
                onFloorChanged: (item) => {
                    this.selectedFloor = item ? item.name : "";
                },
                onTicketTypePrompt: this.showTicketTypesDialog,
                channels: ["NO_CHANNEL"],
                multiSelectEnabled: this.seatingSettings.useBestAvaliable ? false : this.seatingSettings.multiSelectEnabled,
                showFullScreenButton: this.seatingSettings.useBestAvaliable ? false : true,
                selectionValidators: this.getValidators(),
                onSelectionInvalid: (errors) => {
                    this.canPurchase = false;
                    this.seatsSelectionErrors = errors.map((x) => {
                        return {
                            type: "SeatsPickerError",
                            text: this.getSelectionErrorMessage(x),
                        };
                    });
                },
                onSelectionValid: () => {
                    this.canPurchase = true;
                    this.seatsSelectionErrors = [];
                },
                onHoldTokenExpired: () => {
                    this.canPurchase = false;
                },
                onSessionInitialized: () => {
                    this.seatsSelectionErrors = [];
                    this.canPurchase = true;
                },
                showFullScreenButton: false,
                availableCategories: this.getAvaliableCategoriesOnMount(),
                showSectionContents: this.seatingSettings.showSectionContents,
                isObjectVisible: (object, extraConfig) => {
                    if (object.type === "seat") {
                        window.parent.postMessage(
                            `{"label": "${object.label}","messageType":"updateSeats", "parent": "${object.labels.parent}"}`,
                            extraConfig.parentUrl
                        );
                    }

                    return true;
                },
                extraConfig: {
                    parentUrl: window.location.origin,
                },
            }).render();

            this.watchSeatPickerWidth();
        },
        // seats.io sizes its iframe in pixels once at render and never re-measures, so a
        // column that narrows afterwards paints outside the card. Re-rendering re-reads it.
        watchSeatPickerWidth() {
            const picker = document.getElementById("seatsPicker");
            if (!picker || typeof ResizeObserver === "undefined") return;

            let lastWidth = Math.round(picker.getBoundingClientRect().width);

            // The entry's own box: getBoundingClientRect here forces a second layout every
            // frame of a drag. Height is ignored, the container being aspect-ratio'd.
            this._seatPickerResizeObserver = new ResizeObserver(([entry]) => {
                const width = Math.round(entry.contentRect.width);
                if (width === lastWidth || width === 0) return;
                lastWidth = width;

                clearTimeout(this._seatPickerResizeTimeout);
                this._seatPickerResizeTimeout = setTimeout(() => this.chart?.rerender(), 150);
            });

            this._seatPickerResizeObserver.observe(picker);
        },
        getAvaliableCategoriesOnMount() {
            return this.seatingSettings.useBestAvaliable ? ["none"] : this.seatingSettings.seatedCategories.map((x) => x.ticketPoolId);
        },
        getOwnParentDisplayName(section, objectType) {
            if (objectType === "GeneralAdmissionArea") return "";

            if (section.toLowerCase().includes("table")) return "Table";

            return "Row";
        },
        showTicketTypesDialog(params, confirmSelection) {
            this.seatSelectionConfirmSelectionCallback = confirmSelection;

            const section = startCase(params.objectToSelect.labels.section);
            const ownType = startCase(params.objectToSelect.objectType);
            const parentType = this.getOwnParentDisplayName(section, params.objectToSelect.objectType);
            const parentNumber = params.objectToSelect.labels.parent;
            const ownNumber = params.objectToSelect.labels.own;

            if (this.isSeatObjectATable(section)) {
                const seatsWithParent = this.allSeatIds[parentNumber];

                const seatInfos = seatsWithParent.map((seatId) => {
                    return this.chart.findObject(seatId);
                });

                Promise.all(seatInfos).then((objectInfos) => {
                    var availableTicketTypes = params.ticketTypes.map((x) => {
                        return {
                            Name: this.inventory[x.ticketType].name,
                            price: this.currencyFormat(this.inventory[x.ticketType].price),
                            ticketTypeId: x.ticketType,
                            bookingType: this.isTicketTypeABundle(x.ticketType) ? "bundle" : "single",
                            parentId: parentNumber,
                        };
                    });

                    const hasBookedSeats = objectInfos.filter((x) => x.status !== "free").length > 0;

                    if (hasBookedSeats) {
                        availableTicketTypes = availableTicketTypes.filter((x) => x.bookingType != "bundle");
                    }

                    this.seatSelectionTicketTypeDialogData = {
                        ticketTypes: availableTicketTypes,
                        sectionName: section,
                        row: `${parentType} ${parentNumber} ${ownType} ${ownNumber}`,
                        seatObject: params.objectToSelect,
                    };

                    this.showTicketDialog = true;
                });
            } else {
                this.seatSelectionTicketTypeDialogData = {
                    ticketTypes: params.ticketTypes.map((x) => {
                        return {
                            Name: this.inventory[x.ticketType].name,
                            price: this.currencyFormat(this.inventory[x.ticketType].price),
                            ticketTypeId: x.ticketType,
                            bookingType: this.isTicketTypeABundle(x.ticketType) ? "bundle" : "single",
                            parentId: parentNumber,
                        };
                    }),
                    sectionName: section,
                    row: `${parentType} ${parentNumber} ${ownType} ${ownNumber}`,
                    seatObject: params.objectToSelect,
                };

                this.showTicketDialog = true;
            }
        },
        selectSeatFromDialog(ticketTypeId, bookingType, parentNumber) {
            const selectionError = [
                {
                    type: "SeatsPickerError",
                    text: "Your selected seats cannot be reserved - adjust selection and try again.",
                },
            ];

            if (bookingType === "bundle") {
                const seatsWithParent = this.allSeatIds[parentNumber];
                this.chart
                    .doSelectObjects(
                        seatsWithParent.map((x) => {
                            return {
                                id: x,
                                ticketType: ticketTypeId,
                            };
                        })
                    )
                    .catch(() => {
                        this.seatsSelectionErrors = selectionError;
                    });
            } else {
                this.seatSelectionConfirmSelectionCallback(ticketTypeId).catch((e) => {
                    this.seatsSelectionErrors = selectionError;
                });
            }

            this.showTicketDialog = false;
        },
        cancelSelectSeatFromDialog() {
            this.showTicketDialog = false;
        },
        quantities(ticketId) {
            const size =
                this.inventory[ticketId].maximumQuantity < this.inventory[ticketId].minimumQuantity
                    ? this.inventory[ticketId].maximumQuantity
                    : this.inventory[ticketId].maximumQuantity - this.inventory[ticketId].minimumQuantity + 1;

            return [...Array(size)].map((_, i) => ({
                key: `${ticketId}-${i}`,
                value: i + this.inventory[ticketId].minimumQuantity,
            }));
        },
        updateCategoriesOnSeatingChart(categories) {
            this.chart.changeConfig({
                availableCategories: categories, // this will deselect objects that belong to an unavailable category
            });
        },
        ticketSelectonOnChangeEvent(event, ticketId) {
            const ticketCount = event.target.value;
            // The v1 desktop quantity dropdown, where the v2 card has a stepper calling addTicket: without this
            // checkout:ticket_select would only ever fire for one arm.
            if (Number(ticketCount) > 0) this.captureTicketSelect();
            if (this.seatingSettings && this.seatingSettings.useBestAvaliable) {
                const categoryidfilter = this.seatingSettings.seatedCategories.filter(
                    (x) => x.ticketTypes.filter((y) => y.ticketType === ticketId).length > 0
                );
                const categoryKey = categoryidfilter[0].ticketPoolId;

                if (!this.bestAvaliableSectionsSelected[categoryKey] && ticketCount > 0) {
                    this.bestAvaliableSectionsSelected[categoryKey] = ticketCount;
                    this.updateCategoriesOnSeatingChart(Object.keys(this.bestAvaliableSectionsSelected));
                    return;
                }

                if (ticketCount == 0) {
                    delete this.bestAvaliableSectionsSelected[categoryKey];
                    if (Object.keys(this.bestAvaliableSectionsSelected).length === 0) {
                        this.updateCategoriesOnSeatingChart(["none"]);
                    } else {
                        this.updateCategoriesOnSeatingChart(Object.keys(this.bestAvaliableSectionsSelected));
                    }
                }
            }
        },
        moveToNextPage() {
            this.pageIndex = this.pageIndex + 1;
            const transitionInMs = 500;
            setTimeout(() => {
                window.scrollTo(0, 0);
            }, transitionInMs);
        },

        moveToPreviousPage() {
            this.pageIndex = this.pageIndex - 1;
            this.showNoTicketsSelectedAlert = false;
            this.showMustPurchaseAdditionalTicketsAlert = false;
        },

        onSocialShareOpen(network, url) {
            trackShare(network, url);
        },

        showNoTicketsSelectedAlertAndSetFocus() {
            this.showNoTicketsSelectedAlert = true;
            this.showExpiredAlert = false;
            this.$nextTick(() => this.$refs.noTicketsSelectedAlert.focus());
        },

        showMustPurchaseAdditionalTicketsAlertAndFocus(message) {
            this.showMustPurchaseAdditionalTicketsAlert = true;
            this.showMustPurchaseAdditionalTicketsAlertMessage = message;
            this.showExpiredAlert = false;
            this.$nextTick(() => this.$refs.mustPurchaseAdditionalTicketsAlert.focus());
        },
        hasSeatAlreadyBeenAddedApartOfABundle(sectionId, parentId, ticketType) {
            return this.seatBundles[this.generateSeatBundleId(sectionId, parentId, ticketType)];
        },
        isTicketTypeABundle(ticketType) {
            return this.inventory[ticketType].name.toLowerCase().includes("table of");
        },
        generateSeatBundleId(sectionId, parentId, ticketType) {
            return `${sectionId}|${parentId}|${ticketType}`;
        },
        isSeatObjectATable(sectionName) {
            return sectionName.toLowerCase().includes("table");
        },
        reserveSeat(seatInfo, selectedItem) {
            const ticketName = this.inventory[selectedItem.ticketType].name;
            const seatParent = seatInfo.labels.parent ?? "";
            const isBundle = this.isTicketTypeABundle(selectedItem.ticketType);
            const section = seatInfo.objectType === "GeneralAdmissionArea" ? seatInfo.label : seatInfo.labels.section ?? "";
            const seatNumber = seatInfo.objectType === "GeneralAdmissionArea" ? "0" : seatInfo.labels.own;
            if (isBundle) {
                if (!this.hasSeatAlreadyBeenAddedApartOfABundle(section, seatParent, selectedItem.ticketType)) {
                    this.seatBundles[this.generateSeatBundleId(section, seatParent, seatInfo.selectedTicketType)] = {
                        section: section,
                        rowNumber: seatParent,
                        price: this.inventory[selectedItem.ticketType].price,
                        parentType: this.getOwnParentDisplayName(section, seatInfo.objectType),
                        isBundle,
                        count: 1,
                    };
                    this.addTicket(selectedItem.ticketType);
                }
            } else {
                this.addTicket(selectedItem.ticketType);
            }
            this.selectedSeats.push({
                id: seatInfo.label,
                name: ticketName,
                price: this.inventory[selectedItem.ticketType].price,
                floor: this.selectedFloor ?? "",
                section: section,
                seatNumber: seatNumber,
                rowNumber: seatParent,
                type: seatInfo.objectType,
                count: 1,
                ticketPoolId: seatInfo.category.key,
                ticketTypeId: seatInfo.selectedTicketType,
                isBundle,
                bundleId: this.generateSeatBundleId(section, seatParent, seatInfo.selectedTicketType),
                parentType: this.getOwnParentDisplayName(section, seatInfo.objectType),
            });
        },
        unReserveSeat(seatInfo, selectedItem) {
            const seatParent = seatInfo.labels.parent ?? "";
            const section = seatInfo.objectType === "GeneralAdmissionArea" ? seatInfo.label : seatInfo.labels.section ?? "";

            if (
                this.isTicketTypeABundle(selectedItem.ticketType) &&
                this.hasSeatAlreadyBeenAddedApartOfABundle(section, seatParent, selectedItem.ticketType)
            ) {
                delete this.seatBundles[this.generateSeatBundleId(section, seatParent, selectedItem.ticketType)];
                const adjacentSeats = this.allSeatIds[seatParent];
                this.chart.deselectObjects(adjacentSeats);
            }

            this.removeTicket(selectedItem.ticketType);
            var index = this.selectedSeats.findIndex((x) => x.id === seatInfo.label && x.ticketTypeId === selectedItem.ticketType);
            if (index > -1) {
                this.selectedSeats.splice(index, 1);
            }
        },
        // What a first tap would give — the minimum, not always one. Set directly
        // because addTicket would scroll on load.
        preselectSoleTicketType() {
            if (this.seatingSettings && this.seatingSettings.seatingChartKey) return;
            // On a return visit a preselected 1 reads as a second ticket, and trips
            // shouldSuppressCartDrawer on load so the cart vanishes.
            if (this.collectionCartTicketCount > 0 || this.eventCartTicketCount > 0) return;

            const onCard = this.session ? visibleTicketIds(this.session.groups) : null;
            const selectable = Object.keys(this.inventory).filter((id) => {
                const item = this.inventory[id];
                return (!onCard || onCard.has(id)) && item.isOnSale && item.isAvailable && item.maximumQuantity > 0 && !this.isLocked(id);
            });
            if (selectable.length !== 1) return;
            if (Object.values(this.requestedInventory).some((quantity) => quantity > 0)) return;

            const soleTicketId = selectable[0];
            const {minimumQuantity, maximumQuantity, pageToShowOn} = this.inventory[soleTicketId];

            if (pageToShowOn) this.pageIndex = pageToShowOn;
            this._solePreselectId = soleTicketId;
            this.requestedInventory[soleTicketId] = Math.min(Math.max(minimumQuantity, 1), maximumQuantity);
        },

        addTicket(inventoryId) {
            this._solePreselectId = null;
            const {minimumQuantity, maximumQuantity} = this.inventory[inventoryId];
            const currentNumberOfTickets = this.requestedInventory[inventoryId];
            // The footer appears on the first selection and can cover the stepper
            // just used; nudge it back into view above the footer.
            const wasEmpty = this.$uiVariant === "v2" && this.anyTicketsSelected === 0;
            if (this.requestedInventory[inventoryId] < maximumQuantity) {
                const numberOfTicketsToAdd = currentNumberOfTickets == 0 ? minimumQuantity : 1;
                this.requestedInventory[inventoryId] += numberOfTicketsToAdd;
                this.captureTicketSelect();
                if (wasEmpty) this.$nextTick(() => this.revealTicketAboveFooter(inventoryId));
            }
        },

        revealTicketAboveFooter(inventoryId) {
            const footer = this.$refs.ctaFooter;
            const card = document.querySelector(`[data-ticket-id="${inventoryId}"]`);
            if (!footer || !card) return;
            // Over-reserve to cover the footer's total-row reveal still animating in.
            const reserved = window.innerHeight - footer.offsetHeight - 32;
            const overlap = card.getBoundingClientRect().bottom - reserved;
            if (overlap > 0) window.scrollBy({ top: overlap, behavior: scrollBehavior() });
        },
        removeTicket(inventoryId) {
            this._solePreselectId = null;
            const {minimumQuantity, maximumQuantity} = this.inventory[inventoryId];
            const currentNumberOfTickets = this.requestedInventory[inventoryId];
            if (this.requestedInventory[inventoryId] != 0) {
                const numberOfTicketsToRemove = currentNumberOfTickets == minimumQuantity ? minimumQuantity : 1;
                this.requestedInventory[inventoryId] -= numberOfTicketsToRemove;
            }
        },
        isRemoveDisabled(inventoryId) {
            return this.requestedInventory[inventoryId] == 0;
        },

        isAddDisabled(inventoryId) {
            return this.requestedInventory[inventoryId] == this.inventory[inventoryId].maximumQuantity;
        },

        // v2 ticket locking — a ticket is "locked" while it depends on at least one
        // other inventory item that has not yet been selected. The submit-time guard
        // in ticketsRequiringOtherInventoriesToBeSelected() catches orphans server-side;
        // this lets the UI reflect the same constraint inline so the stepper, card
        // styling, and "Requires …" label can react to prerequisite changes.
        isLocked(inventoryId) {
            const item = this.inventory[inventoryId];
            if (!item) return false;
            const requires = item.requiresAnyOfTheseInventoriesToBeSelected;
            if (!requires || requires.length === 0) return false;
            return !requires.some((reqId) => this.requestedInventory[reqId] > 0);
        },

        lockedRequirementLabel(inventoryId) {
            const item = this.inventory[inventoryId];
            if (!item) return "";
            const requires = item.requiresAnyOfTheseInventoriesToBeSelected;
            if (!requires || requires.length === 0) return "";
            const names = requires.map((reqId) => this.inventory[reqId]?.name).filter(Boolean);
            if (names.length === 0) return "";
            if (names.length === 1) return `Requires ${names[0]}`;
            const head = names.slice(0, -1).join(", ");
            return `Requires ${head} or ${names[names.length - 1]}`;
        },

        activeClass(inventoryId) {
            return this.requestedInventory[inventoryId] > 0 ? "active" : "";
        },

        showOnCurrentPage(inventoryId) {
            return this.pageInventory[inventoryId]?.pageToShowOn == this.pageIndex;
        },

        expandTicketDescription(ticketId) {
            this.descriptionExpanded[ticketId] = true;
        },

        // Engines without ::details-content leave collapsed content unlaid-out, so it
        // measures zero. Native toggle also covers the tab path, which sets `open`.
        onTicketGroupToggle(event) {
            if (event.target.open) this.measureTicketDescriptions();
        },

        // Overflow is only knowable after layout, and changes with width.
        measureTicketDescriptions() {
            const root = this._v2TicketCard || document;
            root.querySelectorAll("[data-ticket-description]").forEach((el) => {
                const ticketId = el.getAttribute("data-ticket-description");
                if (this.descriptionExpanded[ticketId]) return;
                this.descriptionOverflowing[ticketId] = el.scrollHeight > el.clientHeight + 1;
            });
        },

        // A jump that barely moves the page still has to show which group answered.
        revealTicketGroup(target) {
            if (target.tagName === "DETAILS" && !target.open) {
                const content = target.querySelector("summary")?.nextElementSibling;
                // Browsers without interpolate-size animate from an explicit height.
                if (content) content.style.setProperty("--content-height", `${content.scrollHeight}px`);
                target.open = true;
            }

            // Flashing now repaints a large area every frame of the coming smooth scroll.
            this.afterScrollSettles(() => this.flashHighlight(target));
        },

        afterScrollSettles(run) {
            clearTimeout(this._v2ScrollSettleTimer);
            if (this._v2OnScrollEnd) {
                window.removeEventListener("scrollend", this._v2OnScrollEnd);
                this._v2OnScrollEnd = null;
            }

            const fire = () => {
                clearTimeout(this._v2ScrollSettleTimer);
                this._v2ScrollSettleTimer = null;
                if (this._v2OnScrollEnd) {
                    window.removeEventListener("scrollend", this._v2OnScrollEnd);
                    this._v2OnScrollEnd = null;
                }
                run();
            };

            if ("onscrollend" in window) {
                this._v2OnScrollEnd = fire;
                window.addEventListener("scrollend", fire, {once: true});
            }
            // scrollend never fires when the jump is too short to start a scroll.
            this._v2ScrollSettleTimer = setTimeout(fire, 600);
        },

        // Styling and duration live in .is-flash-highlighted's custom properties.
        flashHighlight(element) {
            if (!element) return;
            // Keyframes are off under reduced motion, so animationend never fires.
            if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

            if (!this._v2FlashAborts) this._v2FlashAborts = new WeakMap();
            // Re-flashing leaves the previous listener pending.
            const pending = this._v2FlashAborts.get(element);
            if (pending) pending.abort();

            element.classList.remove("is-flash-highlighted");
            // Force a reflow so re-flashing the same element replays the animation.
            void element.offsetWidth;
            element.classList.add("is-flash-highlighted");

            const controller = new AbortController();
            this._v2FlashAborts.set(element, controller);
            element.addEventListener(
                "animationend",
                (event) => {
                    // Bubbles: a child's animation must not strip the tint.
                    if (event.target !== element) return;
                    element.classList.remove("is-flash-highlighted");
                    controller.abort();
                },
                {signal: controller.signal},
            );
        },

        // Safari lacks CSS interpolate-size, so set --content-height for the height fallback.
        setDisclosureHeight(event) {
            const content = event.currentTarget.nextElementSibling;
            if (content) {
                content.style.setProperty("--content-height", `${content.scrollHeight}px`);
            }
        },

        groupSelectedCount(inventoryIds) {
            return inventoryIds.reduce((sum, id) => sum + (this.requestedInventory[id] || 0), 0);
        },

        groupOptionsCount(inventoryIds) {
            return inventoryIds.filter((id) => this.showOnCurrentPage(id)).length;
        },

        // The badge renders an icon and a bare number, so this names it for a reader.
        groupOptionsLabel(inventoryIds) {
            const count = this.groupOptionsCount(inventoryIds);
            return `${count} ${count === 1 ? "option" : "options"}`;
        },

        redirectToPage(redirectTo) {
            window.location = redirectTo;
        },
        getSelectionErrorMessage(errorType) {
            switch (errorType) {
                case "noOrphanSeats":
                    return "No orphan seats are allowed";
                case "consecutiveSeats":
                    return "You must select consecutive seats";
            }
        },
        handleReservationFailure() {
            this.showModalDialog(
                "Tickets are reserved",
                "The ticket(s) you requested are currently reserved by other customers. Try selecting a smaller number of tickets, a different ticket type or try again later."
            );
        },

        handleReservationNotSucceeded(status, errors) {
            if (status === ReservationStatus.Rejected) {
                this.captureCartAddFailure("reservation_rejected");
                this.showModalDialog(
                    "We couldn't add these tickets",
                    errors?.[0]?.message ?? "These tickets can't be added to your cart."
                );
                return;
            }

            this.captureCartAddFailure("reservation_failed");
            this.handleReservationFailure();
        },

        handleError(error) {
            if (error instanceof AxiosRedirectIntercepted) return;

            console.error("Something went wrong", error);

            window.oztixPostHog?.captureException?.(error, {feature: "event"});

            this.showModalDialog("Something went wrong", "We couldn't complete your request. Please refresh the page to continue.");
        },

        showModalDialog(title, message) {
            this.showModal = true;
            this.modalTitle = title;
            this.modalMessage = message;
        },

        showAddToCalendarDialog() {
            this.showAddToCalendar = true;
            document.body.className += " " + "modal-open";
        },

        closeAddToCalendarDialog() {
            window.location.reload(true);
        },

        showBusy() {
            this.busy = true;
            return Promise.resolve();
        },
        hideBusy() {
            this.busy = false;
            this.busyAction = null;
        },
        validate() {
            if (!this.anyTicketsSelected) {
                this.showNoTicketsSelectedAlertAndSetFocus();
                return false;
            }

            var invalidTickets = this.ticketsRequiringOtherInventoriesToBeSelected();
            if (invalidTickets.length) {
                const message = `${invalidTickets.join(", ")} can only be purchased with an additional ticket selection`;
                this.showMustPurchaseAdditionalTicketsAlertAndFocus(message);
                return false;
            }

            return true;
        },

        ticketsRequiringOtherInventoriesToBeSelected() {
            var selectedInventoryIds = this.selectedTickets.map((ticket) => ticket.inventoryId);

            const ticketRequiresOtherInventoriesToBeSelected = (ticket) =>
                ticket.requiresAnyOfTheseInventoriesToBeSelected.length &&
                ticket.requiresAnyOfTheseInventoriesToBeSelected.every((inventoryId) => selectedInventoryIds.indexOf(inventoryId) == -1);

            return this.selectedTickets.filter(ticketRequiresOtherInventoriesToBeSelected).map((ticket) => ticket.name);
        },

        buildAnalyticsData(cartId) {
            return {
                eventId: this.sessionEventId,
                eventName: this.sessionEventName,
                inventory: this.selectedTickets.map((ticket) => ({
                    id: ticket.inventoryId,
                    name: ticket.name,
                    quantity: ticket.quantity,
                    price: ticket.price,
                    priceBase: ticket.priceBase.toFixed(2),
                })),
                total: this.total,
                cartId: cartId,
                venueName: this.venueDescription,
            };
        },

        submit(event) {
            if (!this.validate()) return;

            let reserved = false;

            this.showBusy()
                .then(this.postTickets(this.reserveUrl ?? event.target.action))
                .then((response) => {
                    const {status, redirectUrl, cartId, cartCreated, errors} = response.data;

                    if (!reservationSucceeded(status)) {
                        this.hideBusy();
                        this.handleReservationNotSucceeded(status, errors);
                        return;
                    }

                    reserved = true;
                    this.captureCartAdd(cartId, cartCreated);

                    const analyticsData = this.buildAnalyticsData(cartId);
                    trackAddToCart(analyticsData);
                    trackBeginCheckout(analyticsData);
                    this.redirectToPage(redirectUrl);
                })
                .catch((error) => {
                    this.hideBusy();
                    if (!reserved && !(error instanceof AxiosRedirectIntercepted))
                        this.captureCartAddFailure("request_failed");
                    this.handleError(error);
                });
        },

        submitWithAction(action) {
            if (!this.validate()) return;

            let reserved = false;

            const form = this.$refs.ticketForm;
            this.busyAction = action;
            this.showBusy()
                .then(this.postTickets(this.reserveUrl ?? form.action, action))
                .then((response) => {
                    const {status, redirectUrl, cartId, cartCreated, errors} = response.data;

                    if (!reservationSucceeded(status)) {
                        this.hideBusy();
                        this.handleReservationNotSucceeded(status, errors);
                        return;
                    }

                    reserved = true;
                    this.captureCartAdd(cartId, cartCreated);

                    const analyticsData = this.buildAnalyticsData(cartId);
                    trackAddToCart(analyticsData);
                    if (action === SubmissionAction.Checkout) {
                        trackBeginCheckout(analyticsData);
                    }

                    if (action === SubmissionAction.AddToCart) {
                        this.handleAddToCartSuccess();
                        this.hideBusy();
                    } else {
                        this.redirectToPage(redirectUrl);
                    }
                })
                .catch((error) => {
                    this.hideBusy();
                    if (!reserved && !(error instanceof AxiosRedirectIntercepted))
                        this.captureCartAddFailure("request_failed");
                    this.handleError(error);
                });
        },

        cartAddContext() {
            const collectionId = this.collectionId ?? null;

            return {
                event_id: this.sessionEventId,
                collection_id: collectionId,
                is_in_collection: Boolean(collectionId),
            };
        },

        // cart_create and cart_item_add are distinct funnel events, and the server says which — a local ticket
        // count guesses wrong for a cart started in another tab. send_instantly: the caller navigates this tick.
        captureCartAdd(cartId, cartCreated) {
            window.oztixPostHog?.capture(
                cartCreated ? "checkout:cart_create" : "checkout:cart_item_add",
                () => ({
                    ...this.cartAddContext(),
                    cart_id: cartId,
                    add_ticket_quantity: this.selectedTickets.reduce((total, ticket) => total + ticket.quantity, 0),
                    add_ticket_total_cents: Math.round((this.total || 0) * 100),
                }),
                {send_instantly: true}
            );
        },

        captureTicketSelect() {
            window.oztixPostHog?.captureOncePerSession("checkout:ticket_select", () => this.cartAddContext(), this.sessionEventId);
        },

        captureCartAddFailure(reason) {
            window.oztixPostHog?.capture("checkout:cart_add_fail", () => ({
                ...this.cartAddContext(),
                failure_reason: reason,
            }));
        },

        handleAddToCartSuccess() {
            const addedCount = Object.values(this.requestedInventory).reduce((sum, qty) => sum + qty, 0);
            Object.keys(this.requestedInventory).forEach((id) => {
                this.requestedInventory[id] = 0;
            });
            this.collectionCartTicketCount += addedCount;
            const key = String(this.sessionEventId ?? "").toLowerCase();
            this.cartTicketCountByEvent = {...this.cartTicketCountByEvent, [key]: (this.cartTicketCountByEvent[key] ?? 0) + addedCount};
            this.showAddedConfirmation = true;
            if (this._addedConfirmationTimer) clearTimeout(this._addedConfirmationTimer);
            this._addedConfirmationTimer = setTimeout(() => {
                this.showAddedConfirmation = false;
                this._addedConfirmationTimer = null;
            }, 2000);

            // Tell the shared Roadie cart drawer (a separate bundle) to refetch.
            // Emitted on window.EventBus so it crosses bundles in dev and prod.
            window.EventBus?.$emit("cart:updated");
        },

        emitSuppressState() {
            window.EventBus?.$emit(this.shouldSuppressCartDrawer ? "cart:suppress" : "cart:unsuppress");
        },

        openCartDrawer() {
            window.EventBus?.$emit("cart:open");
        },

        cancelSelection() {
            // Seated events hold the picked seats on the chart; zeroing the quantities
            // alone leaves them selected, so clear the chart too (this also runs the
            // unReserveSeat deselect path).
            if (this.chart) {
                this.chart.clearSelection();
            }
            this.selectedSeats = [];
            this.seatBundles = {};
            Object.keys(this.requestedInventory).forEach((id) => {
                this.requestedInventory[id] = 0;
            });
        },

        scrollToTicketSelection() {
            const el = document.getElementById("select-tickets");
            if (el) el.scrollIntoView({ behavior: scrollBehavior(), block: "start" });
        },

        postTickets(url, action = null) {
            var reservedSeats = [];
            if (this.seatingSettings && !this.reserveUrl) {
                this.holdToken = this.chart.holdToken;

                if (this.seatingSettings.useBestAvaliable) {
                    reservedSeats = Object.keys(this.requestedInventoryToPost).map((inventoryId) => {
                        const categoryidfilter = this.seatingSettings.seatedCategories.filter(
                            (x) => x.ticketTypes.filter((y) => y.ticketType === inventoryId).length > 0
                        );
                        const categoryKey = categoryidfilter[0].ticketPoolId;

                        return {
                            count: this.requestedInventoryToPost[inventoryId],
                            ticketPoolId: categoryKey,
                            ticketTypeId: inventoryId,
                        };
                    });
                } else {
                    reservedSeats = this.selectedSeats.map((seat) => {
                        return {
                            ...seat,
                        };
                    });
                }
            }

            return () =>
                axios.post(url, {
                    // The id is keyed to the page's own event so it can't travel to a sibling session,
                    // but the code itself can: IndexPost resolves it against the event being reserved.
                    accessCodeId: this.reserveUrl ? null : this.accessCode.accessCodeId,
                    accessCode: this.accessCode.accessCode,
                    autoScanForOrder: this.autoScanEnabled,
                    requestedInventory: this.requestedInventoryToPost,
                    reservedSeats: reservedSeats,
                    partnerCustomerId: window?.Converge?.identity?.personId,
                    holdToken: this.chart ? this.chart.holdToken : "",
                    ...(this.sessionInCollection
                        ? {
                              collectionId: this.collectionId,
                              collectionUrl: this.collectionUrl,
                          }
                        : {}),
                    ...(action ? {action} : {}),
                });
        },

        sessionSection(name) {
            return this.sessionSections ? (this.sessionSections[name] ?? "") : null;
        },

        handleRemindMeClick() {
            this.showRemindMeModal = true;
        },

        closeRemindMeModal() {
            this.showRemindMeModal = false;
        },

        getButtonPriceDescription(deliveryFeeMessage) {
            // A fee line on a $0 order reads as a catch. With no selection yet, judge
            // it on the whole event.
            const nothingToCharge = this.anyTicketsSelected ? this.total === 0 : this.everythingIsFree;
            if (nothingToCharge) return "";

            const bookingClause = this.anyTicketsSelected && this.totalBookingFees > 0
                ? `Price includes a $${this.totalBookingFees.toFixed(2)} booking fee.`
                : `Price includes booking fees.`;

            const additionalFees = [this.totalTransactionFeeMessage, deliveryFeeMessage].filter(Boolean);
            if (additionalFees.length === 0) return bookingClause;

            const additional = additionalFees.join(" and ");
            const verb = additionalFees.length > 1 ? "apply" : "applies";
            return `${bookingClause} ${additional.charAt(0).toUpperCase()}${additional.slice(1)} ${verb} per order.`;
        },

        updateAutoScanCallback(newValue) {
            this.autoScanPreference = newValue;

            // The saved default is also what the seller expects the ticket form to be set to, so
            // move the current order's checkbox with it rather than waiting for the next page load.
            if (this.$uiVariant === "v2") this.autoScanEnabled = newValue;
        },

        getHighestTicketPrice() {
            if (!this.selectedTickets || this.selectedTickets.length === 0) return 0;
            return Math.max(...this.selectedTickets.map((ticket) => ticket.price));
        },

        getHighestTicketPriceBase() {
            if (!this.selectedTickets || this.selectedTickets.length === 0) return 0;
            return Math.max(...this.selectedTickets.map((ticket) => ticket.priceBase));
        },

        getCheapestTicketPrice() {
            const availableTickets = this.inventory
                ? Object.values(this.inventory).filter((ticket) => ticket.isOnSale && ticket.isAvailable)
                : [];
            if (!availableTickets || availableTickets.length === 0) return 0;
            return Math.min(...availableTickets.map((ticket) => ticket.price));
        },

        getCheapestTicketPriceBase() {
            const availableTickets = this.inventory
                ? Object.values(this.inventory).filter((ticket) => ticket.isOnSale && ticket.isAvailable)
                : [];
            if (!availableTickets || availableTickets.length === 0) return 0;
            return Math.min(...availableTickets.map((ticket) => Big(ticket.price).minus(ticket.bookingFee).toNumber()));
        },

        getLowestPossibleTransactionFee() {
            if (!this.charges || this.charges.length === 0) return 0;

            const availableTickets = this.inventory
                ? Object.values(this.inventory).filter((ticket) => ticket.isOnSale && ticket.isAvailable)
                : [];

            if (!availableTickets || availableTickets.length === 0) return 0;

            const cheapestTicketPrice = this.getCheapestTicketPrice();
            const cheapestTicketPriceBase = this.getCheapestTicketPriceBase();

            const calculateFeeForAmount = (charge, baseAmount) => {
                if (!charge.chargeBrackets || charge.chargeBrackets.length === 0) return 0;

                const baseAmountBig = Big(baseAmount);
                const applicableBracket = charge.chargeBrackets
                    ?.sort((a, b) => b.minimumAmount - a.minimumAmount)
                    ?.find((bracket) => baseAmountBig.gte(bracket.minimumAmount));

                if (!applicableBracket) return 0;

                const chargeAmount =
                    charge.chargeType === chargeType.percentage
                        ? baseAmount <= 0
                            ? 0
                            : Big(applicableBracket.amount).div(100).times(baseAmount).toNumber()
                        : applicableBracket.amount;

                return chargeAmount;
            };

            const calculateTotalFeeForTicket = (ticketPrice, ticketPriceBase) => {
                return this.charges.reduce((total, charge) => {
                    const baseAmount =
                        charge.calculationMethod === chargeCalculationMethod.highestTicketPrice ? ticketPriceBase : ticketPrice;
                    return total + calculateFeeForAmount(charge, baseAmount);
                }, 0);
            };

            const feeForCheapest = calculateTotalFeeForTicket(cheapestTicketPrice, cheapestTicketPriceBase);

            // If the fee is $0, find the next tier that has a non-zero fee
            if (feeForCheapest === 0) {
                // Get all available ticket prices above the cheapest
                const availableTicketPrices = availableTickets
                    .map((ticket) => ({
                        price: ticket.price,
                        priceBase: Big(ticket.price).minus(ticket.bookingFee).toNumber(),
                    }))
                    .filter((ticket) => ticket.price > cheapestTicketPrice)
                    .sort((a, b) => a.price - b.price);

                // Only look for higher tiers if there are actually other ticket prices available
                if (availableTicketPrices.length > 0) {
                    // Try each available ticket price to see if it results in a non-zero fee
                    for (const ticket of availableTicketPrices) {
                        const testFee = calculateTotalFeeForTicket(ticket.price, ticket.priceBase);
                        if (testFee > 0) {
                            return testFee;
                        }
                    }

                    // If no available ticket prices result in fees, check charge bracket minimums
                    // but only for amounts that correspond to actual available ticket prices
                    const allMinimumAmounts = this.charges
                        .flatMap((charge) => charge.chargeBrackets?.map((b) => b.minimumAmount) || [])
                        .filter((amount) => amount > cheapestTicketPrice)
                        .sort((a, b) => a - b);

                    // Try each bracket tier in ascending order until we find one with a non-zero fee
                    for (const testAmount of allMinimumAmounts) {
                        // For bracket minimums, use the same value for both price and priceBase
                        // as we don't have context of what the booking fee would be
                        const testFee = calculateTotalFeeForTicket(testAmount, testAmount);
                        if (testFee > 0) {
                            return testFee;
                        }
                    }
                }

                // If there are no other ticket prices available, or all fees are $0, return 0
                return 0;
            }

            return feeForCheapest;
        },

        currencyFormat,
    },
    directives: {
        addCurrentQueryStringToLinks: {
            mounted(el) {
                addCurrentQueryStringToLinks(el);
            },
        },
    },
});

if (isV2) {
    vm.component("ph-arrow-left", PhArrowLeft);
    vm.component("ph-arrow-right", PhArrowRight);
    vm.component("ph-arrow-up-right", PhArrowUpRight);
    vm.component("ph-bell-ringing", PhBellRinging);
    vm.component("ph-calendar-dots", PhCalendarDots);
    vm.component("ph-calendar-plus", PhCalendarPlus);
    vm.component("ph-calendar-star", PhCalendarStar);
    vm.component("ph-caret-down", PhCaretDown);
    vm.component("ph-check", PhCheck);
    vm.component("ph-check-circle", PhCheckCircle);
    vm.component("ph-circle-notch", PhCircleNotch);
    vm.component("ph-clock", PhClock);
    vm.component("ph-info", PhInfo);
    vm.component("ph-envelope", PhEnvelope);
    vm.component("ph-envelope-simple", PhEnvelopeSimple);
    vm.component("ph-facebook-logo", PhFacebookLogo);
    vm.component("ph-identification-card", PhIdentificationCard);
    vm.component("ph-key", PhKey);
    vm.component("ph-link", PhLink);
    vm.component("ph-lock", PhLock);
    vm.component("ph-map-pin", PhMapPin);
    vm.component("ph-navigation-arrow", PhNavigationArrow);
    vm.component("ph-seat", PhSeat);
    vm.component("ph-share-network", PhShareNetwork);
    vm.component("ph-shopping-bag", PhShoppingBag);
    vm.component("ph-ticket", PhTicket);
    vm.component("ph-warning-circle", PhWarningCircle);
    vm.component("ph-whatsapp-logo", PhWhatsappLogo);
    vm.component("ph-x-logo", PhXLogo);
}

vm.mount("#events-page");

if (document.readyState !== 'loading') initImageEnhancements();
else document.addEventListener('DOMContentLoaded', () => initImageEnhancements());
